/* ODKAZ NA DOPORUČENÍ — sdílená komponenta (složka klienta, analýza, prezentace).
 *
 * Vznikla vytažením `ReferralLinkButton` z components/crm/ClientDetailRaynet.js
 * (14. 10. 2026, majitel: „na konci plánu rovnou vygenerovat odkaz na
 * doporučení… ať je tam možnost na tu klientku poslat rovnou"). Chování složky
 * zůstává: klik → odkaz → CELÁ zpráva do schránky (UAT 8/2026: „je tam jenom
 * odkaz, nevím, co poslat") → upravitelný text + WhatsApp + Kopírovat.
 * Přibylo: QR kód (klient ho naskenuje rovnou u stolu), SMS a e-mail s jeho
 * telefonem / e-mailem, a jazyk zprávy (vietnamská klientka českého poradce).
 *
 * Logika (API, paměť odkazu, mailto/sms, QR) je v lib/doporuceni/
 * odkazNaDoporuceni.js — tady je jen vzhled. Snímek prezentace plánu skládá
 * z týchž kusů (`QrKod`, `TlacitkaSdileni`, `useOdkazNaDoporuceni`) vlastní
 * rozvržení v barvách motivu prezentace.
 *
 * Barvy jen z tokenů appky. QR je VŽDY tmavý na světlém (čtečky v telefonech
 * inverzní kód nečtou spolehlivě) — proto `--kino-bg` na `--kino-ink`: tahle
 * dvojice je v každém motivu tmavá × světlá s kontrastem přes 12 : 1.
 *
 * JAZYK ODKAZU (kontrola 14. 10. 2026): QR i zpráva nesou `?lang=` jazyka
 * zprávy — stránka /d/… se klientce otevře v tom jazyce, ve kterém zprávu
 * četla (dřív vietnamská zpráva → česká stránka).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion, useReducedMotion } from "motion/react";
import { toast } from "sonner";
import { ChevronRight, Copy, Mail, MessageCircle, MessageSquareText, RotateCcw, Users } from "lucide-react";
import { osloveniProZpravu } from "@/lib/czechNames";
import { zpravaDoporuceni, smsDoporuceni, JAZYKY_ZPRAVY } from "@/lib/crm/zpravaDoporuceni";
import { JAZYKY, jazykUI } from "@/lib/prekladyUI";
import {
  kopirujText, odkazSJazykem, odkazyKSdileni, odkazZPameti, qrCesta, qrMatice, QR_OKRAJ, textDoporuceni, ziskejOdkazNaDoporuceni,
} from "@/lib/doporuceni/odkazNaDoporuceni";

/** Jazyk: zadaný, jinak jazyk UI; cokoli neznámého → čeština. */
export function jazykNebo(lang) {
  if (JAZYKY.includes(lang)) return lang;
  const ui = jazykUI();
  return JAZYKY.includes(ui) ? ui : "cs";
}

/**
 * Stav odkazu pro jednoho klienta. `automaticky` = získat hned po připojení
 * (konec prezentace); jinak až po `ziskej()` (tlačítko ve složce a v analýze).
 * Hotový odkaz z paměti je k dispozici rovnou, bez dalšího volání.
 *
 * Stav nese, PRO KOHO je (`pro`). Když se klient vymění (složka otevřená na
 * jiném kontaktu), starý odkaz se neukáže ani na okamžik a pozdě doběhlá
 * odpověď pro předchozího klienta se zahodí — jinak by poradce poslal
 * klientce odkaz, pod kterým se doporučení připíšou někomu jinému.
 */
export function useOdkazNaDoporuceni({ contactId, jmeno, automaticky = false } = {}) {
  const id = String(contactId ?? "").trim();
  const [stav, setStav] = useState({ pro: "", faze: "nic", odkaz: "" });
  let aktualni = stav;
  if (stav.pro !== id) {
    const z = odkazZPameti(id);
    aktualni = z ? { pro: id, faze: "hotovo", odkaz: z } : { pro: id, faze: "nic", odkaz: "" };
  }
  const zivy = useRef(true);
  useEffect(() => {
    zivy.current = true;
    return () => { zivy.current = false; };
  }, []);

  /* Vrací `{ odkaz, chyba }` — volající (klik na tlačítko) potřebuje výsledek
   * hned, ne až po dalším renderu. `chyba` = hláška backendu, je-li.
   * Stav nese i HTTP `status` chyby: 404/403 (cizí klient — manažer
   * prezentuje klienta jiného poradce) se opakováním nespraví, snímek
   * prezentace pak „Zkusit znovu“ vůbec nenabídne. */
  const ziskej = useCallback(async () => {
    if (!id) return { odkaz: "", chyba: "" };
    setStav((s) => (s.pro === id && s.faze === "hotovo" ? s : { pro: id, faze: "nacitam", odkaz: "" }));
    try {
      const odkaz = await ziskejOdkazNaDoporuceni({ contactId: id, jmeno });
      if (zivy.current) setStav((s) => (s.pro === id ? { pro: id, faze: "hotovo", odkaz } : s));
      return { odkaz, chyba: "" };
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const chyba = typeof detail === "string" ? detail : "";
      const status = Number(e?.response?.status) || 0;
      if (zivy.current) setStav((s) => (s.pro === id ? { pro: id, faze: "chyba", odkaz: "", chyba, status } : s));
      return { odkaz: "", chyba, status };
    }
  }, [id, jmeno]);

  useEffect(() => {
    if (automaticky && id && aktualni.faze === "nic") ziskej();
  }, [automaticky, id]); // eslint-disable-line react-hooks/exhaustive-deps

  return { faze: aktualni.faze, odkaz: aktualni.odkaz, chyba: aktualni.chyba || "", status: aktualni.status || 0, ziskej };
}

/**
 * QR kód jako SVG (vykreslený lokálně). `tmava` / `svetla` = barvy modulů
 * a podkladu; výchozí je tmavá × světlá kinopaleta motivu. `velikost` = px
 * nebo CSS délka („min(36vh, 300px)“ na plátně prezentace).
 * `animace` = má se kód jemně objevit; nevyplněno = podle prefers-reduced-
 * motion. Snímek prezentace posílá svůj ANIM (zohlední i skrytou záložku,
 * kde by uspaný rAF nechal kód na opacity 0 — planPrezentace/motiv.js).
 * Tichá zóna (QR_OKRAJ = 4 moduly) je uvnitř SVG, proto obal bez paddingu.
 */
export function QrKod({ text, velikost = 132, tmava = "var(--kino-bg)", svetla = "var(--kino-ink)", popis = "", testid, zaobleni = 12, animace }) {
  const reduced = useReducedMotion();
  const matice = useMemo(() => qrMatice(text), [text]);
  const cesta = useMemo(() => qrCesta(matice), [matice]);
  if (!matice || !cesta) return null;
  const strana = matice.velikost + 2 * QR_OKRAJ;
  const pohyb = animace === undefined ? !reduced : !!animace;
  return (
    <motion.div
      initial={pohyb ? { opacity: 0, scale: 0.98 } : false}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
      data-testid={testid}
      style={{ width: velikost, height: velikost, flexShrink: 0, background: svetla, borderRadius: zaobleni, overflow: "hidden", boxSizing: "border-box" }}>
      <svg role="img" aria-label={popis || undefined} viewBox={`0 0 ${strana} ${strana}`} width="100%" height="100%"
        shapeRendering="crispEdges" style={{ display: "block" }}>
        {popis ? <title>{popis}</title> : null}
        <rect width={strana} height={strana} fill={svetla} />
        <path d={cesta} fill={tmava} data-qr-cesta="" />
      </svg>
    </motion.div>
  );
}

/* Vzhled tlačítek: výchozí = tokeny appky. Prezentace pošle barvy motivu.
 * 15. 10. 2026 (SPEC-STAVBA §2, §7): WhatsApp už NENÍ zelený — zelená znamená
 * „hotovo“, ne „pošli“. Všechny kanály jsou stejně šedé (vedlejší akce). */
const BARVY_APP = {
  pozadi: "var(--surface-2)", text: "var(--ink)", okraj: "1px solid var(--line)",
  hlavniPozadi: "var(--surface-2)", hlavniText: "var(--ink)", hlavniOkraj: "1px solid var(--line)",
};

/**
 * WhatsApp / SMS / e-mail / Kopírovat s hotovou zprávou. Tlačítko, které by
 * nikam nevedlo (SMS bez telefonu, e-mail bez adresy), se nevykreslí.
 * `onKanal(kanal)` (15. 10. 2026) = klik na WhatsApp / SMS / e-mail („wa“,
 * „sms“, „email“) — Závěr analýzy si zapíše „předáno“. Kopírovat nic nehlásí.
 */
/* `popisky` (15. 10. 2026) = krátké názvy kanálů pro Závěr analýzy
 * („WhatsApp · SMS · E-mailem · Kopírovat“); bez propu beze změny. */
export function TlacitkaSdileni({ zprava, zpravaSms, telefon, email, predmet, lang, testid = "referral", barvy = BARVY_APP, velke = false, onKanal = null, popisky = null }) {
  const jazyk = jazykNebo(lang);
  const tx = (k) => (popisky && popisky[k]) || textDoporuceni(k, jazyk);
  const odkazy = odkazyKSdileni({ zprava, zpravaSms, telefon, email, predmet });
  const b = { ...BARVY_APP, ...barvy };
  const tridy = `inline-flex items-center justify-center gap-1.5 px-3 rounded-full font-semibold min-h-[44px] ${velke ? "text-[14px] sm:min-h-[40px]" : "text-xs sm:min-h-[34px]"}`;
  const hlas = (kanal) => (e) => { e.stopPropagation(); if (typeof onKanal === "function") onKanal(kanal); };
  const kopiruj = async (e) => {
    e.stopPropagation();
    if (await kopirujText(zprava)) toast.success(tx("zkopirovano"));
    else toast.error(tx("kopirovaniSelhalo"));
  };
  if (!String(zprava ?? "").trim()) return null;
  return (
    <div className="flex gap-1.5 flex-wrap" data-testid={`${testid}-tlacitka`}>
      {odkazy.whatsapp ? (
        <a href={odkazy.whatsapp} target="_blank" rel="noopener noreferrer" onClick={hlas("wa")} data-testid={`${testid}-whatsapp`}
          className={`${tridy} flex-1`} style={{ background: b.hlavniPozadi, color: b.hlavniText, border: b.hlavniOkraj }}>
          <MessageCircle size={14} aria-hidden="true" /> {tx("whatsapp")}
        </a>
      ) : null}
      {odkazy.sms ? (
        <a href={odkazy.sms} onClick={hlas("sms")} data-testid={`${testid}-sms`}
          className={tridy} style={{ background: b.pozadi, color: b.text, border: b.okraj }}>
          <MessageSquareText size={14} aria-hidden="true" /> {tx("sms")}
        </a>
      ) : null}
      {odkazy.email ? (
        <a href={odkazy.email} onClick={hlas("email")} data-testid={`${testid}-email`}
          className={tridy} style={{ background: b.pozadi, color: b.text, border: b.okraj }}>
          <Mail size={14} aria-hidden="true" /> {tx("email")}
        </a>
      ) : null}
      <button type="button" onClick={kopiruj} data-testid={`${testid}-copy`}
        className={tridy} style={{ background: b.pozadi, color: b.text, border: b.okraj, cursor: "pointer" }}>
        <Copy size={14} aria-hidden="true" /> {tx("kopirovat")}
      </button>
    </div>
  );
}

/** Zpráva pro klienta v jazyce zprávy (oslovení skloňuje jen čeština).
 *  Odkaz ve zprávě nese týž jazyk (`?lang=`) — stránka /d/… se otevře v něm. */
export function zpravaProKlienta({ jmeno, odkaz, jazyk, odmena = null, poradce = "" }) {
  return zpravaDoporuceni({ osloveni: osloveniProZpravu(jmeno, jazyk), odkaz: odkazSJazykem(odkaz, jazyk), lang: jazyk, odmena, poradce });
}

/** Krátká SMS k témuž odkazu (SPEC §5.2) — odměna i podpis jako ve zprávě. */
export function smsProKlienta({ jmeno, odkaz, jazyk, odmena = null, poradce = "" }) {
  return smsDoporuceni({ osloveni: osloveniProZpravu(jmeno, jazyk), odkaz: odkazSJazykem(odkaz, jazyk), lang: jazyk, odmena, poradce });
}

/**
 * Tlačítko → odkaz, zpráva ve schránce, QR, upravitelný text a odeslání.
 *
 * @param {string} contactId  id klienta v CRM — BEZ NĚJ SE NEVYKRESLÍ NIC
 * @param {string} jmeno      celé jméno (oslovení ve zprávě)
 * @param {string} telefon    telefon klienta (WhatsApp/SMS); prázdný = bez SMS
 * @param {string} email      e-mail klienta; neplatný/prázdný = bez e-mailu
 * @param {string} popisek    text tlačítka (výchozí „Vygenerovat doporučení")
 * @param {string} testid     předpona data-testid (výchozí „referral")
 * @param {object|null} odmena  odměna tipaři (useOdmenaTipare u volajícího); null = jen poděkování
 * @param {string} poradce    podpis zprávy (usePodpisDoporuceni u volajícího); "" = bez podpisu
 *   Hook se volá u volajícího, ne tady: ProfileContext táhne lib/api (axios)
 *   a tenhle modul importují i testy kina a plánu, které API nepotřebují.
 */
export default function OdkazNaDoporuceni(props) {
  /* Jiný klient = čistý stav (žádná rozepsaná zpráva s odkazem předchozího). */
  return <OdkazNaDoporuceniKlienta key={String(props?.contactId ?? "")} {...props} />;
}

function OdkazNaDoporuceniKlienta({ contactId, jmeno = "", telefon = "", email = "", lang, popisek, testid = "referral", testidTlacitka, odmena = null, poradce = "" }) {
  const jazykUi = jazykNebo(lang);
  const tx = (k) => textDoporuceni(k, jazykUi);
  const { faze, odkaz, ziskej } = useOdkazNaDoporuceni({ contactId, jmeno });
  const [otevreno, setOtevreno] = useState(false);
  const [jazykZpravy, setJazykZpravy] = useState(() => (JAZYKY_ZPRAVY.includes(jazykUi) ? jazykUi : "cs"));
  // Hotová zpráva zůstane na obrazovce, ať si ji poradce může přečíst,
  // upravit a teprve pak poslat — schránka se dá snadno přepsat něčím jiným.
  const [zprava, setZprava] = useState("");

  if (!String(contactId ?? "").trim()) return null;

  const otevri = async () => {
    const vysledek = odkaz ? { odkaz } : await ziskej();
    const url = vysledek.odkaz;
    if (!url) { toast.error(vysledek.chyba || tx("chyba")); return; }
    // UAT 8/2026: do schránky patří CELÁ ZPRÁVA, ne holá URL.
    const msg = zpravaProKlienta({ jmeno, odkaz: url, jazyk: jazykZpravy, odmena, poradce });
    setZprava(msg);
    setOtevreno(true);
    if (await kopirujText(msg)) {
      toast.success(tx("zpravaZkopirovana"), { description: tx("zpravaZkopirovanaPopis") });
    }
  };

  const zmenJazyk = (j) => {
    setJazykZpravy(j);
    // Jiný jazyk = jiná zpráva. Ruční úpravy v původním jazyce by v novém
    // nedávaly smysl, proto se text složí znovu.
    if (odkaz) setZprava(zpravaProKlienta({ jmeno, odkaz, jazyk: j, odmena, poradce }));
  };

  if (otevreno && odkaz) {
    return (
      <div className="w-full flex flex-col gap-2" data-testid={`${testid}-panel`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <QrKod text={odkazSJazykem(odkaz, jazykZpravy)} velikost={112} popis={tx("qrPopis")} testid={`${testid}-qr`} zaobleni={10} />
          <div className="min-w-0 flex-1 flex flex-col gap-1.5">
            <label className="text-drobny inline-flex items-center gap-1.5" style={{ color: "var(--muted)" }}>
              {tx("jazykZpravy")}
              <select value={jazykZpravy} onChange={(e) => zmenJazyk(e.target.value)} data-testid={`${testid}-jazyk`}
                className="rounded-lg px-2 text-[16px] sm:text-xs font-semibold min-h-[44px] sm:min-h-[30px]"
                style={{ background: "var(--surface-2)", color: "var(--ink)", border: "1px solid var(--line)" }}>
                {JAZYKY_ZPRAVY.map((j) => <option key={j} value={j}>{j.toUpperCase()}</option>)}
              </select>
            </label>
          </div>
        </div>
        <textarea
          value={zprava}
          onChange={(e) => setZprava(e.target.value)}
          rows={9}
          data-testid={`${testid}-message`}
          aria-label={tx("textZpravy")}
          lang={jazykZpravy}
          className="w-full rounded-lg p-2.5 text-[16px] sm:text-drobny leading-relaxed resize-y"
          style={{ background: "var(--surface-2)", border: "1px solid var(--line)", color: "var(--ink)" }}
        />
        <TlacitkaSdileni zprava={zprava} telefon={telefon} email={email} predmet={textDoporuceni("predmet", jazykZpravy)}
          zpravaSms={smsProKlienta({ jmeno, odkaz, jazyk: jazykZpravy, odmena, poradce })}
          lang={jazykUi} testid={testid} />
      </div>
    );
  }

  const nacitam = faze === "nacitam";
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); otevri(); }}
      disabled={nacitam}
      data-testid={testidTlacitka || `generate-${testid}-link`}
      className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition-shadow hover:shadow-sm min-h-[44px] sm:min-h-[36px]"
      style={{ background: "var(--accent-soft)", color: "var(--accent-ink)", border: "1px solid color-mix(in srgb, var(--accent) 45%, transparent)", cursor: nacitam ? "wait" : "pointer" }}
    >
      {faze === "chyba" ? <RotateCcw size={13} aria-hidden="true" /> : <Users size={13} aria-hidden="true" />}
      {nacitam ? tx("tvorim") : faze === "chyba" ? tx("zkusitZnovu") : (popisek || tx("tlacitko"))}
      <ChevronRight size={13} className="ml-auto opacity-60" aria-hidden="true" />
    </button>
  );
}
